import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { db, storage } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const AboutPreview = () => {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const [content, setContent] = useState({
    floatingTitle: '',
    floatingSubtitle: '',
    badge: '',
    name: 'Cathy Roldán',
    subtitle: '',
    desc1: '',
    desc2: '',
    tags: [] as string[],
    button: '',
    imageUrl: 'https://readdy.ai/api/search-image?query=confident%20latina%20businesswoman%20entrepreneur%20digital%20mentor%20professional%20headshot%20warm%20smile%20modern%20office%20background%20purple%20blue%20lighting%20elegant%20professional%20attire&width=400&height=400&seq=cathy1&orientation=squarish'
  });

  const [tagsString, setTagsString] = useState('');

  useEffect(() => {
    // Cargar traducciones por defecto
    const defaultTags = t('home.aboutPreview.tags', { returnObjects: true }) as string[];
    setContent({
      floatingTitle: t('home.aboutPreview.floating.title'),
      floatingSubtitle: t('home.aboutPreview.floating.subtitle'),
      badge: t('home.aboutPreview.badge'),
      name: 'Cathy Roldán',
      subtitle: t('home.aboutPreview.subtitle'),
      desc1: t('home.aboutPreview.desc1'),
      desc2: t('home.aboutPreview.desc2'),
      tags: Array.isArray(defaultTags) ? defaultTags : [],
      button: t('home.aboutPreview.button'),
      imageUrl: 'https://readdy.ai/api/search-image?query=confident%20latina%20businesswoman%20entrepreneur%20digital%20mentor%20professional%20headshot%20warm%20smile%20modern%20office%20background%20purple%20blue%20lighting%20elegant%20professional%20attire&width=400&height=400&seq=cathy1&orientation=squarish'
    });
    setTagsString(Array.isArray(defaultTags) ? defaultTags.join(', ') : '');

    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', 'about');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as typeof content;
          setContent(data);
          setTagsString(data.tags.join(', '));
        }
      } catch (error) {
        console.error('Error fetching about content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [t]);

  const handleSave = async () => {
    setUploading(true);
    try {
      let imageUrl = content.imageUrl;

      if (imageFile) {
        const storageRef = ref(storage, `landing/about_${Date.now()}_${imageFile.name}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        imageUrl = await getDownloadURL(snapshot.ref);
      }

      const updatedContent = {
        ...content,
        imageUrl,
        tags: tagsString.split(',').map(s => s.trim()).filter(s => s !== '')
      };
      
      await setDoc(doc(db, 'landing_content', 'about'), updatedContent);
      setContent(updatedContent);
      setIsEditing(false);
      setImageFile(null);
    } catch (error) {
      console.error('Error saving about content:', error);
      alert('Error al guardar los cambios.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="relative py-24 bg-[#030006] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_0%_50%,rgba(140,82,255,0.15)_0%,transparent_60%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_100%_50%,rgba(0,194,255,0.08)_0%,transparent_60%)] pointer-events-none" />

      {/* Botones de Control para Admin */}
      {isAdmin && (
        <div className="absolute top-20 right-4 z-50 flex gap-2">
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <i className="ri-edit-line" />
              Editar Sección
            </button>
          ) : (
            <>
              <button 
                onClick={handleSave}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
                disabled={uploading}
              >
                {uploading ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-save-line" />
                )}
                {uploading ? "Guardando..." : "Guardar"}
              </button>
              <button 
                onClick={() => { setIsEditing(false); setImageFile(null); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
                disabled={uploading}
              >
                <i className="ri-close-line" />
                Cancelar
              </button>
            </>
          )}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Image Side */}
          <div className="flex-1 flex justify-center relative">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#8c52ff] to-[#00c2ff] blur-2xl opacity-30 scale-110" />
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: 'conic-gradient(from 0deg, #8c52ff, #FF2DAA, #00c2ff, #8c52ff)',
                  padding: '3px',
                  borderRadius: '50%',
                }}
              >
                <div className="w-full h-full rounded-full bg-[#030006]" />
              </div>
              <img
                src={content.imageUrl}
                alt="Cathy Roldán - Fundadora"
                className="relative w-64 h-64 md:w-80 md:h-80 rounded-full object-cover object-top border-4 border-[#8c52ff]/30"
              />
              
              {isEditing && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 rounded-full">
                  <div className="w-4/5 text-center p-4">
                    <p className="text-white text-xs mb-2">Sube una nueva imagen:</p>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files ? e.target.files[0] : null)}
                      className="w-full bg-white/10 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none mb-2"
                    />
                    <p className="text-white/50 text-xs">O pega la URL:</p>
                    <input 
                      type="text" 
                      value={content.imageUrl} 
                      onChange={(e) => setContent({...content, imageUrl: e.target.value})}
                      className="w-full bg-white/10 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              )}
              
              {isEditing ? (
                <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-[#8c52ff] to-[#FF2DAA] rounded-2xl px-4 py-2 shadow-xl space-y-1">
                  <input 
                    type="text" 
                    value={content.floatingTitle} 
                    onChange={(e) => setContent({...content, floatingTitle: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded px-2 py-0.5 text-white text-xs font-bold focus:outline-none"
                    placeholder="Título Flotante"
                  />
                  <input 
                    type="text" 
                    value={content.floatingSubtitle} 
                    onChange={(e) => setContent({...content, floatingSubtitle: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded px-2 py-0.5 text-white/70 text-xs focus:outline-none"
                    placeholder="Subtítulo Flotante"
                  />
                </div>
              ) : (
                <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-[#8c52ff] to-[#FF2DAA] rounded-2xl px-4 py-3 shadow-xl">
                  <div className="text-white text-xs font-bold">{content.floatingTitle}</div>
                  <div className="text-white/70 text-xs">{content.floatingSubtitle}</div>
                </div>
              )}
            </div>
          </div>

          {/* Content Side */}
          <div className="flex-1 text-center lg:text-left">
            {isEditing ? (
              <div className="space-y-3 mb-4">
                <input 
                  type="text" 
                  value={content.badge} 
                  onChange={(e) => setContent({...content, badge: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-1 text-[#8c52ff] text-xs font-bold uppercase focus:outline-none"
                />
                <input 
                  type="text" 
                  value={content.name} 
                  onChange={(e) => setContent({...content, name: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-3xl font-black focus:outline-none"
                />
                <input 
                  type="text" 
                  value={content.subtitle} 
                  onChange={(e) => setContent({...content, subtitle: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-[#00c2ff] text-base font-semibold focus:outline-none"
                />
              </div>
            ) : (
              <>
                <p className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest mb-3">{content.badge}</p>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-2">{content.name}</h2>
                <h3 className="text-[#00c2ff] text-base md:text-lg font-semibold mb-6">{content.subtitle}</h3>
              </>
            )}

            {isEditing ? (
              <div className="space-y-3 mb-6">
                <textarea 
                  value={content.desc1} 
                  onChange={(e) => setContent({...content, desc1: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white/70 text-sm focus:outline-none h-20"
                />
                <textarea 
                  value={content.desc2} 
                  onChange={(e) => setContent({...content, desc2: e.target.value})}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white/70 text-sm focus:outline-none h-20"
                />
              </div>
            ) : (
              <div className="space-y-4 mb-8">
                <p className="text-white/70 text-sm md:text-base leading-relaxed">{content.desc1}</p>
                <p className="text-white/70 text-sm md:text-base leading-relaxed">{content.desc2}</p>
              </div>
            )}

            {isEditing ? (
              <div className="mb-6">
                <label className="text-white/50 text-xs block mb-1">Etiquetas (separadas por coma)</label>
                <input 
                  type="text" 
                  value={tagsString} 
                  onChange={(e) => setTagsString(e.target.value)}
                  className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-sm focus:outline-none"
                />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mb-8 justify-center lg:justify-start">
                {content.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-white/5 border border-white/10 text-white/70 text-xs px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {isEditing ? (
              <input 
                type="text" 
                value={content.button} 
                onChange={(e) => setContent({...content, button: e.target.value})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-sm focus:outline-none mb-4"
              />
            ) : (
              <div className="flex justify-center lg:justify-start">
                <Link
                  to="/about"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white font-bold px-6 py-3 rounded-full hover:opacity-90 hover:scale-105 transition-all duration-300 text-sm shadow-lg shadow-purple-500/20 cursor-pointer"
                >
                  <i className="ri-user-star-line" />
                  {content.button}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;
